import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getPortalJobs, getPortalJob, type PortalJob, type PortalJobDetail } from '@lib/api/portal';
import { PullToRefresh } from '@/components/common';

const openMaps = (address: string) =>
  window.open(`https://maps.google.com/?q=${encodeURIComponent(address)}`, '_blank');

const day = (s?: string | null) => (s || '').slice(0, 10);
const fmtDate = (s?: string | null) =>
  s ? new Date(day(s) + 'T12:00:00').toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

const statusChip = (status: string) => {
  const s = (status || '').toLowerCase();
  const cls = s === 'in_progress' || s === 'in progress' ? 'bg-blue-100 text-blue-700'
    : s === 'completed' ? 'bg-green-100 text-green-700'
    : s === 'on hold' ? 'bg-yellow-100 text-yellow-700'
    : 'bg-gray-100 text-gray-600';
  return <span className={`shrink-0 text-xs px-2 py-0.5 rounded-full font-medium capitalize ${cls}`}>{s.replace('_', ' ')}</span>;
};

// My Jobs: list of assigned jobs; tap → full detail (tasks, site info, safety
// notes, schedule, worker-visible documents) from GET /me/jobs/:id.
export const EmployeeJobsPage = () => {
  const { jobId } = useParams<{ jobId?: string }>();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState<PortalJob[]>([]);
  const [detail, setDetail] = useState<PortalJobDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    setError(null);
    if (jobId) {
      getPortalJob(Number(jobId))
        .then(setDetail)
        .catch(() => setError('Could not load this job.'))
        .finally(() => setLoading(false));
    } else {
      setDetail(null);
      getPortalJobs()
        .then(setJobs)
        .catch(() => setError('Could not load your jobs.'))
        .finally(() => setLoading(false));
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [jobId]);

  if (loading) return (
    <div className="space-y-3">
      {[1, 2, 3].map(i => <div key={i} className="h-20 animate-pulse rounded-xl bg-gray-100" />)}
    </div>
  );
  if (error) return (
    <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center space-y-3">
      <span className="material-symbols-outlined text-red-400 text-4xl">error</span>
      <p className="text-sm text-red-700">{error}</p>
      <button onClick={() => (jobId ? navigate('/portal/employee/jobs') : load())}
        className="text-sm font-semibold text-red-600 border border-red-300 rounded-lg px-4 py-1.5">
        {jobId ? 'Back to my jobs' : 'Retry'}
      </button>
    </div>
  );

  // ── Detail view ──
  if (detail) {
    const j = detail.job;
    return (
      <PullToRefresh onRefresh={load}>
        <div className="space-y-4 pb-4">
          <button onClick={() => navigate('/portal/employee/jobs')}
            className="flex items-center gap-1 text-sm text-[#C77D3F] font-medium">
            <span className="material-symbols-outlined text-base">arrow_back</span> My jobs
          </button>

          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-start justify-between gap-2">
              <h1 className="text-lg font-bold text-[#2C1810]">{j.title}</h1>
              {statusChip(j.status)}
            </div>
            {j.site_address && (
              <button type="button" onClick={() => openMaps(j.site_address!)}
                className="mt-2 flex items-center gap-1 text-sm text-[#C77D3F] active:opacity-70">
                <span className="material-symbols-outlined text-base">location_on</span>
                {j.site_address}{j.site_city ? `, ${j.site_city}` : ''}
              </button>
            )}
            <p className="text-xs text-gray-400 mt-1.5">{fmtDate(j.start_date)} – {fmtDate(j.end_date)}</p>
            {j.description && <p className="text-sm text-gray-600 mt-3 border-t border-gray-100 pt-3">{j.description}</p>}
          </div>

          {/* Safety first */}
          {j.safety_notes && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <h2 className="font-bold text-red-800 text-sm mb-1 flex items-center gap-1.5">
                <span className="material-symbols-outlined text-lg">health_and_safety</span> Safety notes
              </h2>
              <p className="text-sm text-red-900 whitespace-pre-wrap">{j.safety_notes}</p>
            </div>
          )}

          {j.client_notes && (
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <h2 className="font-semibold text-gray-900 text-sm mb-1 flex items-center gap-1.5">
                <span className="material-symbols-outlined text-[#C77D3F] text-lg">record_voice_over</span> Client notes
              </h2>
              <p className="text-sm text-gray-600 whitespace-pre-wrap">{j.client_notes}</p>
            </div>
          )}

          {/* Tasks */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <h2 className="font-semibold text-gray-900 text-sm mb-2 flex items-center gap-1.5">
              <span className="material-symbols-outlined text-[#C77D3F] text-lg">checklist</span>
              Tasks
            </h2>
            {detail.tasks.length === 0 ? (
              <p className="text-sm text-gray-500">No tasks listed for this job.</p>
            ) : (
              <div className="space-y-2">
                {detail.tasks.map(t => (
                  <div key={t.id} className="flex items-start gap-2.5">
                    <span className={`material-symbols-outlined text-lg mt-0.5 ${t.status === 'completed' ? 'text-green-600' : 'text-gray-300'}`}>
                      {t.status === 'completed' ? 'check_circle' : 'radio_button_unchecked'}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium ${t.status === 'completed' ? 'text-gray-400 line-through' : 'text-gray-900'}`}>{t.title}</p>
                      {t.description && <p className="text-xs text-gray-400">{t.description}</p>}
                    </div>
                    {t.priority === 'high' && t.status !== 'completed' && (
                      <span className="text-xs px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 font-medium">high</span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Recent schedule */}
          {detail.schedule.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <h2 className="font-semibold text-gray-900 text-sm mb-2 flex items-center gap-1.5">
                <span className="material-symbols-outlined text-[#C77D3F] text-lg">calendar_month</span>
                Schedule
              </h2>
              <div className="space-y-1.5">
                {detail.schedule.map(s => (
                  <div key={s.id} className="flex items-center gap-2 text-sm">
                    <span className="text-gray-700">{fmtDate(s.scheduled_date)}{s.end_date && day(s.end_date) !== day(s.scheduled_date) ? ` – ${fmtDate(s.end_date)}` : ''}</span>
                    {s.start_time && <span className="text-xs text-gray-400">{String(s.start_time).slice(0, 5)}{s.end_time ? `–${String(s.end_time).slice(0, 5)}` : ''}</span>}
                    <span className="ml-auto">{statusChip(s.status)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Documents */}
          {detail.documents.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <h2 className="font-semibold text-gray-900 text-sm mb-2 flex items-center gap-1.5">
                <span className="material-symbols-outlined text-[#C77D3F] text-lg">folder</span>
                Documents
              </h2>
              <div className="divide-y divide-gray-50">
                {detail.documents.map(d => {
                  const href = d.file_path ? `/${String(d.file_path).replace(/^\//, '')}` : undefined;
                  const row = (
                    <>
                      <span className="material-symbols-outlined text-[#C77D3F] text-lg">description</span>
                      <span className="flex-1 min-w-0 text-sm text-gray-900 truncate">{d.file_name || d.title || 'Document'}</span>
                      {href && <span className="material-symbols-outlined text-gray-300 text-base">open_in_new</span>}
                    </>
                  );
                  return href
                    ? <a key={d.id} href={href} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2.5 py-2 active:bg-gray-50">{row}</a>
                    : <div key={d.id} className="flex items-center gap-2.5 py-2">{row}</div>;
                })}
              </div>
            </div>
          )}
        </div>
      </PullToRefresh>
    );
  }

  // ── List view ──
  return (
    <PullToRefresh onRefresh={load}>
      <div className="space-y-4 pb-4">
        <h1 className="text-xl font-bold text-[#2C1810]">My Jobs</h1>
        {jobs.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-10 text-center">
            <span className="material-symbols-outlined text-gray-300 text-5xl">construction</span>
            <p className="text-sm font-medium text-gray-500 mt-3">No jobs assigned yet</p>
          </div>
        ) : (
          <div className="space-y-2">
            {jobs.map(j => (
              <button
                key={j.id}
                onClick={() => navigate(`/portal/employee/jobs/${j.id}`)}
                className="w-full bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3 text-left active:bg-gray-50"
              >
                <div className="w-10 h-10 rounded-lg bg-[#C77D3F]/10 flex items-center justify-center flex-shrink-0">
                  <span className="material-symbols-outlined text-[#C77D3F] text-xl">construction</span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900 text-sm truncate">{j.title}</p>
                  {j.site_address && <p className="text-xs text-gray-400 truncate">{j.site_address}</p>}
                </div>
                {statusChip(j.status)}
                <span className="material-symbols-outlined text-gray-300">chevron_right</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </PullToRefresh>
  );
};
